/**
 * plans.js — the self-serve pricing tiers as data (the entitlement matrix).
 *
 * COPY OF RECORD for the numbers: the public pricing page
 * (machfivemagnet_hugo/layouts/_default/pricing.html). If a price or limit
 * changes there, it changes here in the same breath — plans.test.js pins the
 * published numbers so drift fails the build instead of lying to a customer.
 *
 * Channel ruling (2026-08-26): managed_client and reseller_org orgs ARE
 * enterprise users — plan resolution is a single ladder, never a bypass
 * branch. A managed client passes the same feature checks a paying
 * self-serve enterprise org passes, including after a program ends
 * (continuation clients stay managed_client, keeping enterprise
 * entitlements at the lower fee — the retention hook, on purpose).
 *
 * `grandfathered` is the pre-billing state: every saas org that existed
 * before enforcement resolves here (unlimited, label "Full access") until
 * the existing-user decision is made. Free-tier defaults apply only to
 * signups after the enforcement cutover (slice three).
 *
 * Limits use `null` for unlimited — never Infinity (JSON round-trips).
 * Money is expressed in whole dollars per month, matching the page.
 *
 * Quota ruling (2026-08-26): a lead is NEVER dropped at the leads_mo cap.
 * The cap defines the over-quota state (banner, 80%/100% notices, upgrade
 * prompt) — capture always succeeds. leads_mo counts every m5mLead row
 * created for the org in the calendar month, regardless of source.
 */

export const ALL_FORMATS = Object.freeze(['chat', 'right_slide', 'modal', 'inline']);

export const PLANS = Object.freeze({
  free: Object.freeze({
    key: 'free',
    label: 'Free',
    prices: Object.freeze({ monthly: 0, annual: 0 }),
    limits: Object.freeze({ magnets: 1, leads_mo: 100, team_members: 1 }),
    features: Object.freeze({
      formats: Object.freeze(['chat']),
      hub_scheduler: false,
      ab_testing: false,
      journeys: false,
      attribution: false,
      crm_sync: false,
      booking: false,
      webhooks: false,
      csv_export: false,
      ad_reporting: false,
    }),
  }),
  pro: Object.freeze({
    key: 'pro',
    label: 'Pro',
    prices: Object.freeze({ monthly: 49, annual: 39 }),
    limits: Object.freeze({ magnets: null, leads_mo: 10000, team_members: 5 }),
    features: Object.freeze({
      formats: ALL_FORMATS,
      hub_scheduler: true,
      ab_testing: true,
      journeys: true,
      attribution: true,
      crm_sync: true,
      booking: true,
      webhooks: true,
      csv_export: true,
      ad_reporting: false,
    }),
  }),
  enterprise: Object.freeze({
    key: 'enterprise',
    label: 'Enterprise',
    prices: Object.freeze({ monthly: 199, annual: 159 }),
    limits: Object.freeze({ magnets: null, leads_mo: 100000, team_members: null }),
    features: Object.freeze({
      formats: ALL_FORMATS,
      hub_scheduler: true,
      ab_testing: true,
      journeys: true,
      attribution: true,
      crm_sync: true,
      booking: true,
      webhooks: true,
      csv_export: true,
      ad_reporting: true,
    }),
  }),
  grandfathered: Object.freeze({
    key: 'grandfathered',
    label: 'Full access',
    prices: null, // not purchasable — a state, not a product
    limits: Object.freeze({ magnets: null, leads_mo: null, team_members: null }),
    features: Object.freeze({
      formats: ALL_FORMATS,
      hub_scheduler: true,
      ab_testing: true,
      journeys: true,
      attribution: true,
      crm_sync: true,
      booking: true,
      webhooks: true,
      csv_export: true,
      ad_reporting: true,
    }),
  }),
});

/** Channels whose orgs are enterprise by definition (the single ladder). */
const ENTERPRISE_CHANNELS = new Set(['reseller_org', 'managed_client']);

/**
 * The Free-tier cutover (slice three, Nick's ruling 2026-08-26): a saas org
 * CREATED on or after this moment with no subscription is on Free; one
 * created before it is grandfathered (full access) until the existing-user
 * decision. Self-serve signups create their workspace at email verification,
 * so the org's created_tz is the signup moment.
 */
export const FREE_TIER_CUTOVER = '2026-08-26T00:00:00.000Z'; // newest pre-existing saas org: 2026-08-23 (Template Library)

/**
 * The one plan-resolution ladder.
 */
export function resolvePlan({ channel, subscriptionPlan = null, orgCreatedTz = null } = {}) {
  if (ENTERPRISE_CHANNELS.has(String(channel || ''))) return 'enterprise';
  if (subscriptionPlan && Object.hasOwn(PLANS, subscriptionPlan)) return subscriptionPlan;
  const created = Date.parse(String(orgCreatedTz || ''));
  if (Number.isFinite(created) && created >= Date.parse(FREE_TIER_CUTOVER)) return 'free';
  return 'grandfathered';
}

export function getPlan(key) {
  return Object.hasOwn(PLANS, key) ? PLANS[key] : PLANS.grandfathered;
}

/** True when the plan includes the feature (formats checked by value). */
export function planAllows(key, feature, value = null) {
  const plan = getPlan(key);
  if (feature === 'format') return plan.features.formats.includes(String(value));
  return plan.features[feature] === true;
}

/** The numeric limit for a dimension; null = unlimited. */
export function planLimit(key, dimension) {
  const plan = getPlan(key);
  return Object.hasOwn(plan.limits, dimension) ? plan.limits[dimension] : null;
}
